import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CloudOff, CloudUpload, Loader2, Trash2, WifiOff } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { subscribeQueue, flushQueue, removeFromQueue, TYPE_LABELS, isClockItem } from '../../lib/offlineQueue';
import { fmtTime, fmtDate } from '../../lib/easerApi';

const FLUSH_INTERVAL_MS = 45000;

/**
 * Banner shown while reports/incidents wait on the device. Retries automatically when the
 * network comes back (online event), when Dolibarr is back (easer:dolibarr-restored), when the app
 * returns to the foreground and every 45 s.
 */
export function OfflineQueueBanner({ onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const busy = useRef(false);

  const flush = useCallback(async (manual = false) => {
    if (busy.current) return;
    busy.current = true;
    setSending(true);
    try {
      const r = await flushQueue();
      r.clockEnds.forEach((c) => { const what = TYPE_LABELS[c.item.type] || 'Pointage'; toast.success(c.data?.already
        ? `${what} « ${c.item.label} » déjà enregistré(e) dans Dolibarr`
        : `${what} « ${c.item.label} » transmis(e) et vérifié(e) dans Dolibarr${c.data?.myschedule ? ' (MySchedule)' : ''}`, { duration: 8000 }); });
      const others = r.sent - r.clockEnds.length;
      if (others > 0) toast.success(`${others} envoi${others > 1 ? 's' : ''} en attente transmis à l'agence`);
      r.dropped.forEach((d) => toast.error(`« ${d.item.label} » refusé par le serveur : ${d.reason}`));
      if (r.unauthorized) onUnauthorized();
      else if (manual && r.stoppedOffline) toast.info('Toujours pas de réseau, nouvel essai automatique plus tard');
      else if (manual && r.dolibarrDown) toast.info('Dolibarr est toujours indisponible : vos envois restent sur le téléphone, nouvel essai automatique à son retour');
      else if (manual && r.sent === 0 && r.failed > 0) toast.error("Échec de l'envoi, nouvel essai plus tard");
    } finally {
      busy.current = false;
      setSending(false);
    }
  }, [onUnauthorized]);

  useEffect(() => subscribeQueue(setItems), []);

  useEffect(() => {
    const goOnline = () => { setOnline(true); flush(); };
    const goOffline = () => setOnline(false);
    const onVisible = () => { if (document.visibilityState === 'visible') flush(); };
    const onRestored = () => flush();
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    window.addEventListener('easer:dolibarr-restored', onRestored);
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(() => flush(), FLUSH_INTERVAL_MS);
    flush();
    return () => {
      window.removeEventListener('easer:dolibarr-restored', onRestored);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [flush]);

  if (!online && items.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-[hsl(var(--secondary))]/60 px-3 py-2 text-sm flex items-center gap-2 mb-4" role="status" data-testid="offline-notice">
        <WifiOff className="h-4 w-4 text-muted-foreground" /> Hors ligne : vos débuts/fins de prestation, comptes-rendus et imprévus seront gardés sur le téléphone et envoyés au retour du réseau.
      </div>
    );
  }
  if (items.length === 0) return null;

  return (
    <div className="rounded-xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5 p-3 text-sm mb-4" role="status" data-testid="offline-queue-banner">
      <div className="flex items-center gap-2">
        <CloudOff className="h-4 w-4 text-[hsl(var(--accent))] flex-shrink-0" />
        <button type="button" className="flex-1 text-left font-medium" onClick={() => setOpen((o) => !o)} data-testid="offline-queue-toggle">
          {items.length} envoi{items.length > 1 ? 's' : ''} en attente{online ? '' : ' · pas de réseau'}
        </button>
        <Button size="sm" variant="outline" className="h-8" onClick={() => flush(true)} disabled={sending} data-testid="offline-queue-send">
          {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CloudUpload className="h-4 w-4 mr-1" />} Envoyer
        </Button>
      </div>
      {open && (
        <ul className="mt-2 divide-y divide-border/60" data-testid="offline-queue-list">
          {items.map((it) => (
            <li key={it.id} className="py-1.5 flex items-center gap-2 text-xs">
              <span className="flex-1 min-w-0">
                <span className="font-medium">{TYPE_LABELS[it.type] || 'Imprévu'}</span> · {it.label}
                <span className="text-muted-foreground"> · {fmtDate(it.createdAt, { day: 'numeric', month: 'short' })} {fmtTime(it.createdAt)}{it.type === 'report' && it.payload.photos?.length ? ` · ${it.payload.photos.length} photo${it.payload.photos.length > 1 ? 's' : ''}` : ''}</span>
                {it.lastError && <span className="text-destructive"> · {it.lastError}</span>}
              </span>
              {!isClockItem(it) && <button type="button" onClick={() => { if (window.confirm('Supprimer cet envoi en attente ?')) removeFromQueue(it.id); }} aria-label="Supprimer" className="p-1 text-muted-foreground hover:text-destructive" data-testid="offline-queue-remove"><Trash2 className="h-3.5 w-3.5" /></button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
